import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { I } from "../../Components/CMI/icons";
import { getIaResumen } from "../../helpers/iaApi";

const SEV_TONE = { high: "neg", medium: "warn", low: "info" };
const SEV_LABEL = { high: "Alta", medium: "Media", low: "Baja" };

/** Construye el snapshot que se envía a la IA según la pantalla. */
function buildSnapshot(screen, finanzas, marketing, proyectos) {
  if (screen === "finanzas") {
    return {
      kpis: finanzas.kpis,
      evolucion: finanzas.evolucion,
      gastos: finanzas.gastos,
      saldos: finanzas.saldos,
      facturas_pendientes: finanzas.facturasPendientes,
      aging_cobros: finanzas.agingCobros,
      dso: finanzas.dso,
      ticket_medio: finanzas.ticketMedioAnual,
      eficiencia: finanzas.eficiencia,
      top_clientes: finanzas.clientesAnual,
      top_paises: finanzas.paisesAnual,
    };
  }
  if (screen === "marketing") {
    return {
      kpis: marketing.kpis,
      leads_mensuales: marketing.leadsMensuales,
      por_fuente: marketing.porFuente,
      embudo: marketing.embudo,
      coste_por_lead: marketing.costePorLead,
      search_console: marketing.gsc,
    };
  }
  if (screen === "proyectos") {
    return {
      kpis: proyectos.kpis,
      evolucion: proyectos.evolucion,
      media_evolucion: proyectos.mediaEvolucion,
      por_tipo: proyectos.porTipo,
      por_pais: proyectos.porPais,
      tareas: proyectos.tareas,
    };
  }
  return {};
}

function snapshotTieneDatos(snapshot) {
  return Object.values(snapshot || {}).some((v) => v !== null && v !== undefined);
}

const ThinkingLine = ({ delay, text }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div className={`thinking-line ${shown ? "on" : ""}`}>
      <I.Check size={12} />
      <span>{text}</span>
    </div>
  );
};

const AIDrawer = ({ open, ctx, onClose }) => {
  const [phase, setPhase] = useState("idle"); // idle | thinking | ready | error
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [reqId, setReqId] = useState(0);

  const finanzas = useSelector((s) => s.DashboardFinanzas);
  const marketing = useSelector((s) => s.DashboardMarketing);
  const proyectos = useSelector((s) => s.DashboardProyectos);

  const fetchResumen = useCallback(async () => {
    if (!ctx?.screen) return;
    const snapshot = buildSnapshot(ctx.screen, finanzas, marketing, proyectos);
    if (!snapshotTieneDatos(snapshot)) {
      setPhase("error");
      setErrorMsg("Aún no se han cargado los datos del dashboard. Espera a que termine de cargar y vuelve a abrir el panel.");
      return;
    }
    setPhase("thinking");
    setData(null);
    setErrorMsg(null);
    try {
      const res = await getIaResumen({
        screen: ctx.screen,
        empresa: ctx.company,
        year: ctx.year,
        snapshot,
      });
      setData(res);
      setPhase("ready");
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "No se pudo generar el resumen.";
      setErrorMsg(msg);
      setPhase("error");
    }
  }, [ctx?.screen, ctx?.company, ctx?.year, finanzas, marketing, proyectos]);

  useEffect(() => {
    if (!open || !ctx) return;
    fetchResumen();
    // Sólo re-lanzar al abrir el drawer, al cambiar de dashboard, o al pulsar Regenerar.
    // No incluimos fetchResumen en deps a propósito (cambia con cada update de Redux).
  }, [open, ctx?.screen, reqId]);

  const regenerar = () => setReqId((n) => n + 1);

  if (!open) return null;

  return (
    <div className={`ai-overlay ${open ? "open" : ""}`} onClick={onClose}>
      <aside className="ai-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="ai-drawer-head">
          <h2>
            <span className="sparkle"><I.Sparkle size={16} /></span>
            Resumen IA
          </h2>
          <div className="meta">
            <span className="ai-bdg">{data?.title || ctx?.title}</span>
            <span>·</span>
            <span>Empresa <strong>{ctx?.company}</strong></span>
            <span>·</span>
            <span>Año <strong>{ctx?.year}</strong></span>
            <span>·</span>
            <span>Generado {new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })} a las {new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <button className="ai-close" onClick={onClose} aria-label="Cerrar">
            <I.X size={16} />
          </button>
        </div>

        <div className="ai-drawer-body">
          {phase === "thinking" && (
            <div className="ai-loading">
              <div className="dots"><span/><span/><span/></div>
              <div className="label">Analizando datos del dashboard…</div>
              <div className="ai-thinking-list">
                <ThinkingLine delay={0}    text="Procesando KPIs y series temporales" />
                <ThinkingLine delay={400}  text="Detectando anomalías y desviaciones" />
                <ThinkingLine delay={800}  text="Comparando contra periodo anterior" />
                <ThinkingLine delay={1200} text="Redactando recomendaciones" />
              </div>
            </div>
          )}

          {phase === "error" && (
            <div className="ai-callout" style={{ borderColor: "var(--danger, #d9534f)" }}>
              <div className="ai-callout-h">
                <span className="sparkle"><I.Triangle size={14} /></span>
                No se pudo generar el resumen
              </div>
              <p className="ai-callout-body">{errorMsg}</p>
            </div>
          )}

          {phase === "ready" && data && (
            <>
              <div className="ai-callout">
                <div className="ai-callout-h">
                  <span className="sparkle"><I.Sparkle size={14} /></span>
                  Resumen ejecutivo
                </div>
                <p className="ai-callout-body">{data.headline}</p>
              </div>

              {Array.isArray(data.highlights) && data.highlights.length > 0 && (
                <div className="ai-block">
                  <div className="h">Aspectos destacados<span className="pill">{data.highlights.length}</span></div>
                  <div className="body">
                    <ul>{data.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>
                  </div>
                </div>
              )}

              {Array.isArray(data.anomalies) && data.anomalies.length > 0 && (
                <div className="ai-block">
                  <div className="h">Anomalías detectadas<span className="pill">{data.anomalies.length}</span></div>
                  <div className="body">
                    <ul className="anom-list">
                      {data.anomalies.map((a, i) => (
                        <li key={i}>
                          <span className={`bdg ${SEV_TONE[a.sev] || "neu"}`}><span className="dot"/>{SEV_LABEL[a.sev] || a.sev}</span>
                          <span>{a.t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {Array.isArray(data.recs) && data.recs.length > 0 && (
                <div className="ai-block">
                  <div className="h">Recomendaciones<span className="pill">{data.recs.length}</span></div>
                  <div className="body">
                    <ol>{data.recs.map((r, i) => <li key={i}>{r}</li>)}</ol>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="ai-drawer-foot">
          <button
            className="btn btn-ai"
            onClick={regenerar}
            disabled={phase === "thinking"}
            title="Volver a generar el resumen"
          >
            <I.Sparkle size={14} /> {phase === "thinking" ? "Generando…" : "Regenerar IA"}
          </button>
          <button className="btn btn-sec" disabled title="Próximamente"><I.Mail size={14}/> Email</button>
          <button className="btn btn-sec" disabled title="Próximamente"><I.Download size={14}/> PDF</button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </aside>
    </div>
  );
};

export default AIDrawer;
